import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CalendarClock, FileText, FileSearch,
  CalendarRange, CheckCircle2, Circle, Clock, Calendar, Users,
  CalendarDays, MessageSquare, ShieldCheck, Ban,
} from 'lucide-react';
import studyBuilderService from '../../services/studyBuilderService';
import { getStudyDraftReviewContext } from '../../services/reviewWorkflowApi';
import SchedulePreviewModal from './SchedulePreviewModal';
import ScheduleReviewCard, { SectionLabel, Chip, Muted } from './ScheduleReviewCard';
import PageHeader from '../Shared/PageHeader';
import { Avatar, Card, EmptyState, PageLoader, StatCard } from '../ui';

/**
 * Dedicated, read-only review of a study draft's design (FECOA review workflow).
 *
 * A purpose-built mirror of the design tool: it renders the full design as a
 * polished top-to-bottom document (status, readiness, arms, and every schedule
 * with its surveys, reminders, site type, windows, and stages). There are no
 * edit controls by construction. The Approve/Deny bar is supplied by ReviewFrame.
 */

const STATUS_META = {
  draft: { label: 'Draft', cls: 'badge-gray', Icon: Clock },
  in_review: { label: 'Pending Review', cls: 'badge-info', Icon: Clock },
  approved: { label: 'Approved', cls: 'bg-teal-50 text-teal-700 ring-teal-600/20 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-400/25', Icon: CheckCircle2 },
  denied: { label: 'Needs Revision', cls: 'badge-danger', Icon: MessageSquare },
  published: { label: 'Published', cls: 'badge-success', Icon: CheckCircle2 },
  superseded: { label: 'Superseded', cls: 'badge-gray', Icon: Clock },
};

const fmtDateTime = (v) => {
  if (!v) return null;
  try { return new Date(v).toLocaleString(); } catch { return v; }
};

const StudyDesignReview = () => {
  const { studyName } = useParams();
  const [draft, setDraft] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [reviewCtx, setReviewCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const drafts = (await studyBuilderService.getStudyDrafts()) || [];
        // Chain-head resolution (FECOA-1831): with an amendment open, the name
        // matches both the published version and the amendment; the review
        // screen must show the amendment (the draft actually under review).
        const found = studyBuilderService.pickCurrentDraft(drafts, studyName) || null;
        if (active) setDraft(found);
        if (found) {
          localStorage.setItem('currentStudyDraftId', String(found.id));
          const [surveyData, templateData, ctx] = await Promise.all([
            studyBuilderService.getSurveysByStudyName(found.name).catch(() => null),
            studyBuilderService.getReminderTemplates(found.id).catch(() => null),
            getStudyDraftReviewContext(found.id).catch(() => null),
          ]);
          if (active) {
            setReminders(templateData?.results || templateData || []);
            setReviewCtx(ctx);
          }
          // The study-name lookup misses surveys when the draft name has
          // drifted from the Study title — resolve unnamed ids individually,
          // inside the loading window so a bare "Survey #id" never flashes.
          const loaded = surveyData?.surveys || [];
          const draftSchedules = Array.isArray(found.data?.schedules) ? found.data.schedules : [];
          const extras = await studyBuilderService
            .resolveMissingScheduleSurveys(draftSchedules, loaded)
            .catch(() => []);
          if (active) {
            setSurveys(extras.length > 0 ? [...loaded, ...extras] : loaded);
          }
        }
      } catch (e) {
        console.error('Error loading study design for review:', e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [studyName]);

  const surveyName = useMemo(() => (id) => {
    const found = surveys.find((s) => s.id === parseInt(id, 10));
    return found ? found.name : `Survey #${id}`;
  }, [surveys]);
  const reminderName = useMemo(() => (id) => {
    const found = reminders.find((r) => r.id === parseInt(id, 10));
    return found ? found.name : `Reminder #${id}`;
  }, [reminders]);

  const data = draft?.data || {};
  const arms = Array.isArray(data.arms) ? data.arms : [];
  const schedules = Array.isArray(data.schedules) ? data.schedules : [];
  const startDay = schedules[0]?.studyStartDay;
  const status = STATUS_META[draft?.review_status] || STATUS_META.draft;
  const StatusIcon = status.Icon;

  const readiness = [
    { label: 'Study start day set', done: startDay !== undefined },
    { label: 'Arms configured', done: arms.length > 0 },
    { label: 'Schedules configured', done: schedules.length > 0 },
    { label: 'Surveys assigned', done: schedules.some((s) => Array.isArray(s?.standardSurveys) && s.standardSurveys.length > 0) },
  ];
  const doneCount = readiness.filter((r) => r.done).length;

  if (loading) {
    return <PageLoader />;
  }

  if (!draft) {
    return (
      <Card className="mx-auto mt-10 max-w-lg">
        <EmptyState icon={FileSearch} title="Could not find this study draft." />
      </Card>
    );
  }

  return (
    <div className="pb-20">
      <PageHeader
        title={draft.name}
        titleAccessory={
          <span className={`badge ${status.cls}`}>
            <StatusIcon className="h-3 w-3" /> {status.label}
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Study Design Review · read-only</span>
            {draft.submitted_by_email && (
              <span className="inline-flex items-center gap-1.5">
                <Avatar name={draft.submitted_by_email} size="xs" />
                {draft.submitted_by_email}
              </span>
            )}
            {draft.submitted_at && (
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" /> {fmtDateTime(draft.submitted_at)}</span>
            )}
          </span>
        }
      >
        {reviewCtx?.assessments?.total > 0 && reviewCtx.assessments.all_approved && (
          <span className="badge badge-success">
            <ShieldCheck className="h-3 w-3" /> All Assessments Approved
          </span>
        )}
        {reviewCtx?.prior_denial_count > 0 && (
          <span className="badge badge-danger">
            <Ban className="h-3 w-3" /> {reviewCtx.prior_denial_count} prior denial{reviewCtx.prior_denial_count === 1 ? '' : 's'}
          </span>
        )}
      </PageHeader>

      <div className="space-y-5">
        {/* Submitter's note */}
        {reviewCtx?.submission_note && (
          <div className="card p-5">
            <SectionLabel>Submitter&apos;s Note</SectionLabel>
            <div className="flex gap-2">
              <MessageSquare className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
              <p className="text-[13.5px] italic text-gray-700 dark:text-gray-200">“{reviewCtx.submission_note}”</p>
            </div>
          </div>
        )}

        {/* Approval message */}
        {reviewCtx?.approval && (
          <div className="card p-5">
            <SectionLabel>Approval</SectionLabel>
            <div className="flex flex-wrap items-center gap-2 text-[13.5px] font-medium text-gray-900 dark:text-gray-50">
              <ShieldCheck className="h-4 w-4 flex-shrink-0 text-success-600 dark:text-success-400" />
              Approved by {reviewCtx.approval.decided_by_name}
              {reviewCtx.approval.decided_at && (
                <span className="font-normal text-gray-500 dark:text-gray-400">· {fmtDateTime(reviewCtx.approval.decided_at)}</span>
              )}
            </div>
            {reviewCtx.approval.message && (
              <p className="ml-6 mt-1 text-[13.5px] italic text-gray-600 dark:text-gray-300">“{reviewCtx.approval.message}”</p>
            )}
          </div>
        )}

        {/* Previous denial history */}
        {reviewCtx?.denial_history?.length > 0 && (
          <div className="card p-5">
            <SectionLabel>Previous Denial History</SectionLabel>
            <ul className="divide-y divide-gray-100 dark:divide-white/[0.06]">
              {reviewCtx.denial_history.map((d, i) => (
                <li key={i} className="py-2.5 text-[13.5px] first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-center gap-2 font-medium text-gray-900 dark:text-gray-50">
                    <Ban className="h-4 w-4 flex-shrink-0 text-danger-600 dark:text-danger-400" />
                    Denied by {d.decided_by_name}
                    {d.decided_at && <span className="font-normal text-gray-500 dark:text-gray-400">· {fmtDateTime(d.decided_at)}</span>}
                  </div>
                  {d.reason && <p className="ml-6 mt-0.5 italic text-gray-600 dark:text-gray-300">“{d.reason}”</p>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard icon={CalendarRange} label="Study start" value={startDay !== undefined ? `Day ${startDay}` : '—'} />
          <StatCard icon={Users} label={arms.length === 1 ? 'Arm' : 'Arms'} value={arms.length} />
          <StatCard icon={CalendarClock} label={schedules.length === 1 ? 'Schedule' : 'Schedules'} value={schedules.length} />
          <StatCard icon={FileText} label={surveys.length === 1 ? 'Survey' : 'Surveys'} value={surveys.length} />
        </div>

        <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
          {/* Schedules */}
          <div className="min-w-0">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Schedules</h2>
              <span className="badge badge-gray tabular-nums">{schedules.length}</span>
              {schedules.length > 0 && (
                <button
                  onClick={() => setPreviewOpen(true)}
                  className="btn btn-secondary btn-sm ml-auto"
                  title="Visualize this schedule against a sample enrollment date"
                >
                  <CalendarDays className="h-4 w-4" />
                  <span>Preview calendar</span>
                </button>
              )}
            </div>
            {schedules.length > 0 ? (
              <div className="space-y-4">
                {schedules.map((s, i) => (
                  <ScheduleReviewCard key={i} schedule={s} surveyName={surveyName} reminderName={reminderName} />
                ))}
              </div>
            ) : (
              <div className="card p-8 text-center">
                <Muted>No schedules configured</Muted>
              </div>
            )}
          </div>

          <aside className="space-y-5">
            {/* Readiness */}
            <div className="card p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Readiness</h2>
                <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">{doneCount}/{readiness.length} complete</span>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-white/[0.08]" aria-hidden="true">
                <div className="h-full rounded-full bg-primary-500" style={{ width: `${(doneCount / readiness.length) * 100}%` }} />
              </div>
              <div className="mt-4 space-y-2.5">
                {readiness.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-[13px]">
                    {r.done
                      ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-success-600 dark:text-success-400" />
                      : <Circle className="h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-600" />}
                    <span className={r.done ? 'text-gray-700 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}>{r.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Arms */}
            <div className="card p-5">
              <div className="mb-3 flex items-center gap-2">
                <h2 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Arms</h2>
                <span className="badge badge-gray tabular-nums">{arms.length}</span>
              </div>
              {arms.length > 0
                ? <div className="flex flex-wrap gap-2">{arms.map((a, i) => <Chip key={i}>{a}</Chip>)}</div>
                : <Muted>No arms configured</Muted>}
            </div>
          </aside>
        </div>
      </div>

      <SchedulePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        schedules={schedules}
        studyStartDay={startDay}
      />
    </div>
  );
};

export default StudyDesignReview;
